import React from 'react';
import { ChevronDown, Download } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Dropdown, PageHeader } from '@insurancemenu/imenu-components-lib';
import { HEADER_SPACING_PRESETS } from '@insurancemenu/imenu-components-lib/dist/components/layouts/utils/pageHeaderSpacing';
import { useRootStore } from 'store/rootStore';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { observer } from 'mobx-react-lite';
import {
  DropdownOptionType,
  OptionsAlignmentTypes,
  ToastColorTypes,
} from '@insurancemenu/imenu-components-lib/dist/types';
import { BiReportFileType } from 'store/pages/bi-groups-dashboard/pageStore';
import axios, { AxiosResponse } from 'axios';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { DropdownButtonStyled, DropdownWrapperStyled } from './styles';

interface CustomReportError extends Error {
  code: string;
}

const BiDashboardHeader: React.FC = observer(() => {
  const { domainStoreV2 } = useRootStore();

  async function getPreSignUrl(sponsorId: string, fileType: BiReportFileType): Promise<AxiosResponse<string>> {
    const res = await axios.get<string>(
      `https://${process.env.REACT_APP_API_BASE_URL}/biReport/Prod/biReportsUrl?sponsorId=${sponsorId}&fileType=${fileType}`,
      {
        withCredentials: true,
      },
    );
    return res;
  }

  async function createReportLinkForDownload(reportType: BiReportFileType): Promise<string> {
    try {
      const res = await getPreSignUrl(domainStoreV2.sponsorId, reportType);
      return res?.data || '';
    } catch (error) {
      if (error instanceof Error && 'code' in error) {
        // Access the custom error code here
        const code: string = (error as CustomReportError).code;
        if (code === '418') {
          toastManager.addToast('File currently is unavailable', ToastColorTypes.WARNING);
        } else {
          toastManager.addToast('Something went wrong during file download', ToastColorTypes.WARNING);
        }
      }
      return '';
    }
  }

  const downloadReport = async (reportType: BiReportFileType): Promise<void> => {
    const reportLink = await createReportLinkForDownload(reportType);
    if (reportLink) {
      downloadByUrl(reportLink);
    }
  };

  const createReportOption = (reportType: BiReportFileType, label: string): DropdownOptionType => {
    return {
      value: reportType,
      icon: Download,
      label,
      onOptionClick: (): void => {
        void downloadReport(reportType);
      },
    };
  };

  const biOptions: DropdownOptionType[] = [
    createReportOption('BI_REPORT_GROUP', 'Group Report'),
    createReportOption('BI_REPORT_QUOTING', 'Quoting Report'),
    createReportOption('BI_REPORT_RENEWAL', 'Renewal Report'),
    createReportOption('AGENCY_REPORT', 'Agency Report'),
    createReportOption('AGENT_REPORT', 'Agent Report'),
    createReportOption('USER_REPORT', 'User Report'),
    createReportOption('AGENCY_AGENT_REPORT', 'Agency Agent Report'),
    createReportOption('AGENCY_USER_REPORT', 'Agency User Report'),
  ];

  return (
    <>
      <PageHeader
        title="BI Dashboard"
        subtitle="Sponsor wide group channel view"
        spacing={HEADER_SPACING_PRESETS.withActionBelow}
      />

      <DropdownWrapperStyled>
        <Dropdown
          name="bi-reports"
          options={biOptions}
          optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
          optionsWidth="22rem"
          closedElement={
            <DropdownButtonStyled color="primary" rightIconProp={<ChevronDown />}>
              Download Report
            </DropdownButtonStyled>
          }
          openedElement={
            <DropdownButtonStyled color="primary" rightIconProp={<ChevronDown />}>
              Download Report
            </DropdownButtonStyled>
          }
        />
      </DropdownWrapperStyled>
    </>
  );
});

export default BiDashboardHeader;
